import BoxWithShadow from "../../common/components/BoxWithShadow";
import { GlobeAltIcon, UserIcon } from "@heroicons/react/24/outline";
import Table from "../../common/components/Table";
import { formatDate } from "../../../utilities/format";
import UserAvatar from "../../common/components/UserAvatar";
import {
  GetReferralNetworkUserAsAdminQuery,
  useGetReferralNetworkUserAsAdminQuery,
} from "../../../lib/apollo/graphql/generated";
import { NetworkOwner } from "../containers/NetworkListContainer";
import CountCard from "../../common/components/CountCard";

interface UpperUserBoxProps {
  owner:
    | GetReferralNetworkUserAsAdminQuery["getReferralNetworkUserAsAdmin"]["caregiver"]
    | GetReferralNetworkUserAsAdminQuery["getReferralNetworkUserAsAdmin"]["guardian"];
}

function UpperUserBox({ owner }: UpperUserBoxProps) {
  const isCaregiver = owner?.__typename === "Caregiver";
  const hasSignInRecord = !!owner?.lastSignInAt;
  const latestSignIn = hasSignInRecord
    ? formatDate(owner?.lastSignInAt, "yyyy-MM-dd")
    : "-";

  return (
    <BoxWithShadow className="gap-5">
      <div className="flex items-center gap-1 font-bold">
        <UserIcon className="aspect-square h-5" />
        <p>상위 사용자</p>
      </div>
      <div className="flex items-center gap-3 text-zinc-700">
        <UserAvatar src={owner?.avatar?.uri} size={100} />
        <div className="flex flex-col gap-1 text-sm">
          <p className="text-lg font-bold">
            {owner?.name} {isCaregiver ? "보호사" : "보호자"} ({owner?.username}
            )
          </p>
          <p>
            직접 받은 리워드: {owner?.totalRewardPoint?.toLocaleString() || 0}점
          </p>
          <p>
            직접 추천인 수: {owner?.invitedUserCount?.toLocaleString() || 0}명
          </p>
          <p className={!hasSignInRecord ? "text-zinc-500" : ""}>
            최근 활동: {latestSignIn}
          </p>
        </div>
      </div>
    </BoxWithShadow>
  );
}

interface NestedNetworkListProps {
  depth: 2 | 3;
  owner: NetworkOwner;
  onOwnerSelect: (owner: NetworkOwner) => void;
  members: {
    id: string;
    name: string;
    username: string | null;
    invitedUserCount: number;
    lastSignInAt: string | null;
    isCaregiver: boolean;
    totalRewardPoint: number | null;
    avatar: {
      uri: string | null;
    };
  }[];
}

function NestedNetworkListBox({
  depth,
  members,
  owner,
  onOwnerSelect,
}: NestedNetworkListProps) {
  return (
    <BoxWithShadow className="h-[750px] min-h-[750px] gap-3">
      <div className="flex items-center gap-1">
        <GlobeAltIcon className="aspect-square w-5" />
        <p className="font-bold">Depth {depth}</p>
      </div>
      <Table.Wrapper>
        <Table.Header
          tabs={[
            { label: "사용자 이름" },
            { label: "사용자 닉네임" },
            { label: "사용자 구분" },
            { label: "리워드 (직접)" },
            { label: "추천인 수" },
            { label: "최근 접속" },
          ]}
        />
        <Table.Body isEmpty={!members?.length}>
          {members?.map((member) => {
            if (!member) {
              return null;
            }

            const onSelect = () => {
              if (!member?.invitedUserCount) {
                alert("해당 사용자는 아무도 초대하지 않았습니다.");
                return;
              }

              onOwnerSelect({
                id: member.id,
                type: member.isCaregiver ? "caregivers" : "guardians",
              });
            };

            return (
              <Table.Row
                key={member.id}
                isSelected={owner.id === member.id}
                onClick={onSelect}
              >
                <Table.Column>{member.name}</Table.Column>
                <Table.Column>{member.username}</Table.Column>
                <Table.Column>
                  {member.isCaregiver ? "보호사" : "보호자"}
                </Table.Column>
                <Table.Column>
                  {member.totalRewardPoint?.toLocaleString() || 0}원
                </Table.Column>
                <Table.Column>{member?.invitedUserCount || 0}명</Table.Column>
                <Table.Column>
                  {formatDate(member?.lastSignInAt, "YY년 MM월 DD일")}
                </Table.Column>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table.Wrapper>
    </BoxWithShadow>
  );
}

interface NetworkDetailsProps {
  owner: NetworkOwner;
  onOwnerSelect: (owner: NetworkOwner) => void;
}

/**
 * The details of the selected network that contains the following:
 * 1. Upper user information (Depth1)
 * 2. Tables of depth2 and depth3 members
 */
export default function NetworkDetails({
  owner,
  onOwnerSelect,
}: NetworkDetailsProps) {
  const isOwnerCaregiver = owner.type === "caregivers";

  const { data } = useGetReferralNetworkUserAsAdminQuery({
    variables: {
      caregiverId: isOwnerCaregiver ? owner.id : null,
      guardianId: isOwnerCaregiver ? null : owner.id,
    },
    skip: !owner.id || !owner.type,
  });

  const network = data?.getReferralNetworkUserAsAdmin;
  const upperUser = isOwnerCaregiver ? network?.caregiver : network?.guardian;

  /**
   * Note: Depth 2, 3 members have to be recursively fetched from the upper user
   */
  const depth2 = [
    ...(upperUser?.invitedCaregivers || []),
    ...(upperUser?.invitedGuardians || []),
  ];

  const depth3 = depth2.flatMap((user) => [
    ...(user?.invitedCaregivers || []),
    ...(user?.invitedGuardians || []),
  ]);

  if (!network || !upperUser) {
    return (
      <p className="flex h-full items-center justify-center rounded-xl bg-white text-xl text-zinc-500">
        나중에 다시 시도해 주세요
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-1">
        <UpperUserBox owner={upperUser} />
        <div className="flex w-1/2 flex-shrink-0 gap-1">
          <CountCard
            label="전체 네트워크 포인트"
            count={network?.totalNetworkRewardPoint || 0}
            unit="원"
          />
          <CountCard
            label="전체 네트워크 맴버 수"
            count={network?.totalNetworkUserCount || 0}
            unit="명"
          />
        </div>
      </div>
      <NestedNetworkListBox
        depth={2}
        members={depth2.map((member) => ({
          id: member.id,
          isCaregiver: member.__typename === "Caregiver",
          name: member.name,
          username: member.username || null,
          invitedUserCount: member.invitedUserCount || 0,
          lastSignInAt: member.lastSignInAt,
          totalRewardPoint: member.totalRewardPoint || 0,
          avatar: {
            uri: member.avatar?.uri || null,
          },
        }))}
        owner={owner}
        onOwnerSelect={onOwnerSelect}
      />
      <NestedNetworkListBox
        depth={3}
        members={depth3.map((member) => ({
          id: member.id,
          isCaregiver: member.__typename === "Caregiver",
          name: member.name,
          username: member.username || null,
          invitedUserCount: member.invitedUserCount || 0,
          lastSignInAt: member.lastSignInAt,
          totalRewardPoint: member.totalRewardPoint || 0,
          avatar: {
            uri: member.avatar?.uri || null,
          },
        }))}
        owner={owner}
        onOwnerSelect={onOwnerSelect}
      />
    </div>
  );
}
